import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TuneIcon from "@mui/icons-material/Tune";
import type { PremiseConfig } from "@shared/types/foyer";
import type {
  OfferOverrideField,
  OfferOverridePatch,
  OfferReach,
  OfferScope,
  ResolvedRoomOffer,
  RoomOffer,
  RoomOfferOverride,
} from "@shared/types/roomOffer";
import {
  offerStatusAt,
  offerStatusReason,
  type OfferStatus,
} from "@shared/roomOffers";
import { resolveRoomOffers } from "@shared/roomOfferLayers";
import { readLocalized } from "@shared/i18n";
import { useCan } from "../auth/AuthContext";
import {
  foyerApi,
  offerScopeKey,
  type OfferScopeRef,
  type RoomOfferBody,
} from "../http/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import LivePreview from "../components/template/LivePreview";
import OfferDialog from "../components/offers/OfferDialog";
import OfferOverrideDialog from "../components/offers/OfferOverrideDialog";
import DemoPaymentsBanner from "../components/offers/DemoPaymentsBanner";
import OfferIcon from "../components/offers/OfferIcon";
import {
  OFFER_KIND_LABEL,
  OFFER_STATUS_LABEL,
  offerProblemsOf,
  type OfferProblem,
} from "../components/offers/offerCopy";
import { tokens } from "../theme";

/**
 * The Offers catalogue — one list and one dialog (`POS-5`).
 *
 * Where it hangs was settled by `docs/room-offers.md` §9 and is not reopened
 * here: a section of `FRONTLAYER_SECTIONS` at `/frontlayer/offers`, not a
 * `ProductKey`. Offers renders into a template's own screens, is authored
 * beside the template's content and draws from the same media library — a
 * product whose every route 404s without another product is a section wearing a
 * product's clothes.
 *
 * ## What the list shows, and why it shows the ones that are off
 *
 * Everything authored. The catalogue route deliberately answers with the
 * disabled rows and the out-of-season ones too, because **a row IS its draft**
 * — a list that hid what it could not currently show would leave an operator no
 * way to reach the spa evening they are preparing for December. The television
 * reads a different URL entirely (POS-3's), so "what is authored" and "what is
 * on screen" are never confused for one another.
 *
 * Status is **derived** by `offerStatusAt()` and never stored. A stored one
 * drifts from the window it was computed from within a week, and then the list
 * claims "Live now" while the editor shows a schedule that ended in March.
 *
 * ## Inherited rows are read-only here
 *
 * A hotel sees its own offers plus its chain's plus Nenda's global starter
 * catalogue, each tagged with a derived `scope`. Reads inherit; writes never
 * do. The row menu is hidden on an inherited row rather than offered and then
 * refused with a 404 — the server would refuse it, and a control that always
 * fails is worse than one that is not there.
 */

/** How long the derived status may be stale before the list recomputes it. */
const STATUS_TICK_MS = 60_000;

export interface OffersPageProps {
  /**
   * Rows as THIS layer sees them, already through `resolveRoomOffers()`.
   *
   * Resolved rather than raw (`POS-14`): the page draws provenance and
   * per-location overrides, and deriving either from the two id fields here
   * would be a second implementation of the rule the television runs.
   */
  offers: ResolvedRoomOffer[];
  /** Which layer this page writes to — decides which rows are editable. */
  scope: OfferScopeRef;
  loading?: boolean;
  canEdit: boolean;
  /** Rendered above the list. The premise branch passes a `LivePreview`. */
  preview?: React.ReactNode;
  /** Named when authoring for a whole customer, so the reach is never a surprise. */
  chainName?: string;
  chainLocationCount?: number;
  /**
   * The QUERIED blast radius — properties and rooms this page's saves reach.
   *
   * Undefined only while the one call is in flight; the banner falls back to
   * the directory's location count until it lands, and never invents a room
   * number. See `companyOfferReach()`.
   */
  chainReach?: OfferReach;
  /** Re-decide an inherited row for this location. Premise scope only. */
  onOverride?: (offer: ResolvedRoomOffer) => void;
  /** Hide, or un-hide, an inherited row at this location. */
  onSuppress?: (offer: ResolvedRoomOffer, suppressed: boolean) => void;
  /**
   * A refusal that happened with no dialog open, said out loud.
   *
   * The list's own switches write to the server, and a failure that only
   * flipped the switch back would leave an operator believing they had hidden
   * a chain campaign that is still on every television in the building.
   */
  notice?: string | null;
  onDismissNotice?: () => void;
  defaultLocale: string;
  /**
   * The premise's own IANA zone.
   *
   * NOT optional decoration: `offerStatusAt` falls back to UTC, and a Stockholm
   * hotel whose offer runs 07:00–22:00 would then read "Scheduled" for the two
   * hours before local midnight while the television is still showing it. A
   * status column that disagrees with the screen is worse than none.
   */
  timezone?: string;
  onCreate: () => void;
  onEdit: (offer: RoomOffer) => void;
  onDelete: (offer: RoomOffer) => void;
  onToggle: (offer: RoomOffer, enabled: boolean) => void;
}

/** Is this row addressable at the layer the page is pointed at? */
export function isOwnRow(offer: RoomOffer, scope: OfferScopeRef): boolean {
  return scope.kind === "premise"
    ? offer.scope === "premise"
    : offer.scope === "company";
}

/** One line saying what a guest actually gets, without opening the row. */
function kindSummary(offer: RoomOffer): string {
  const action = offer.action;
  const price =
    "price" in action && action.price
      ? `${action.price.amount} ${action.price.currency}`
      : null;
  if (action.kind === "promotion") return "Nothing to press";
  if (action.kind === "order") {
    const where =
      action.fulfil === "deliver" ? "to the room" : "collect at the desk";
    return [price, where].filter(Boolean).join(" · ");
  }
  const timetable =
    action.slots.mode === "asap"
      ? "as soon as possible"
      : `${action.slots.startTime}–${action.slots.endTime}, ${action.slots.capacity} per time`;
  return [price, timetable].filter(Boolean).join(" · ");
}

/**
 * Where an inherited row was authored, in the operator's words (`POS-14`).
 *
 * "Company" and "Nenda" were POS-5's labels for the same two layers; these say
 * the same thing as a provenance rather than as a scope name, because the
 * question the chip answers is "who decides this?" and the answer for a hotel
 * is "not you". The CMS chrome is English throughout — see `offerCopy`.
 */
const ORIGIN_LABEL: Record<OfferScope, string> = {
  premise: "This location",
  company: "From the chain",
  global: "From Nenda",
};

/** What each re-decided field is called in the "Overridden here" chip. */
const OVERRIDE_FIELD_LABEL: Record<OfferOverrideField, string> = {
  price: "price",
  availability: "dates",
  imageUrl: "picture",
};

/**
 * The blast radius, in words, from the QUERIED counts (`POS-14`).
 *
 * Real numbers or none: while the query is in flight the banner falls back to
 * the directory's location count and says nothing about rooms, because an
 * invented room number is worse than a missing one — this sentence is the last
 * thing an operator reads before publishing to every building the chain owns,
 * and the mistake is only visible on a television somewhere else.
 */
function blastRadius(reach?: OfferReach, fallbackLocations?: number): string {
  const locations = reach ? reach.locations : (fallbackLocations ?? 0);
  const places = locations === 1 ? "1 location" : `all ${locations} locations`;
  if (!reach) return places;
  const rooms = reach.rooms === 1 ? "1 room" : `${reach.rooms} rooms`;
  return `${places}, ${rooms}`;
}

const STATUS_COLOUR: Record<OfferStatus, string> = {
  live: tokens.success,
  scheduled: tokens.brand,
  ended: tokens.inkFaint,
  off: tokens.inkFaint,
};

export function OffersPage({
  offers,
  scope,
  loading,
  canEdit,
  preview,
  chainName,
  chainLocationCount,
  chainReach,
  defaultLocale,
  timezone,
  onCreate,
  onEdit,
  onDelete,
  onToggle,
  onOverride,
  onSuppress,
  notice,
  onDismissNotice,
}: OffersPageProps) {
  // Status is a function of the clock, so the list has to be told the clock
  // moved. Without this a row scheduled for 18:00 keeps saying "Scheduled" on
  // a CMS somebody left open all afternoon.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), STATUS_TICK_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Promotions"
        title="Offers"
        subtitle="What a guest can order, book or simply read about, from the television in their room."
        actions={
          canEdit ? (
            <Button
              variant="contained"
              size="small"
              endIcon={<AddIcon />}
              onClick={onCreate}
              data-testid="offers-new"
            >
              New offer
            </Button>
          ) : undefined
        }
      />

      {/* POS-8, §6: wherever the demo payment provider is active, say so. It
          renders nothing when the money is real. */}
      <DemoPaymentsBanner />

      {chainName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1.25,
            mb: 2,
            borderRadius: "8px",
            border: `1px solid ${tokens.brand}`,
            bgcolor: tokens.brandTintSoft,
          }}
        >
          <Typography sx={{ fontSize: 14, color: tokens.ink }}>
            Editing <strong>{chainName}</strong> — offers saved here reach{" "}
            <strong data-testid="offers-reach">
              {blastRadius(chainReach, chainLocationCount)}
            </strong>
            {chainReach && chainReach.premises.length > 0 && (
              <>
                {": "}
                {chainReach.premises
                  .map((p) => `${p.premiseName} (${p.rooms})`)
                  .join(", ")}
              </>
            )}
            . A location can reprice or switch one off for itself.
          </Typography>
        </Box>
      )}

      {notice && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={onDismissNotice}
          data-testid="offers-notice"
        >
          {notice}
        </Alert>
      )}

      {preview && <Box sx={{ mb: 2 }}>{preview}</Box>}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Offer</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>What the guest gets</TableCell>
              <TableCell>Picks</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={64} align="center">
                On
              </TableCell>
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.map((offer) => {
              const own = !offer.inherited;
              // A row this location has hidden is OFF here whatever its window
              // says. Deriving the status from the schedule alone would print
              // "Live now" beside a switch that is off, and an operator will
              // believe the column over the switch.
              const statusReason = offerStatusReason(offer, now, timezone);
              const status: OfferStatus = offer.suppressed
                ? "off"
                : offerStatusAt(offer, now, timezone);
              const editable = canEdit && own;
              // Overriding is a PREMISE act. On a chain page the rows are the
              // chain's own plus Nenda's, and re-deciding one of those "for
              // everyone" is simply editing it.
              const overridable =
                canEdit && offer.inherited && scope.kind === "premise";
              return (
                <TableRow
                  key={offer.id}
                  hover
                  data-testid={`offer-row-${offer.id}`}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1.25}>
                      <Box sx={{ color: tokens.inkMuted, flex: "none" }}>
                        <OfferIcon
                          name={offer.items[0]?.icon ?? "generic"}
                          size={20}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: tokens.ink,
                          }}
                        >
                          {readLocalized(offer.title, defaultLocale) ||
                            "Untitled"}
                        </Typography>
                        {!own && (
                          <Stack
                            direction="row"
                            gap={0.5}
                            flexWrap="wrap"
                            sx={{ mt: 0.25 }}
                          >
                            {/* Where it came from, on every inherited row.
                                Without it a hotel cannot tell the spa evening
                                it wrote from the one its chain wrote, and the
                                first thing it does with the chain's is try to
                                edit it. */}
                            <Chip
                              size="small"
                              variant="outlined"
                              label={ORIGIN_LABEL[offer.origin]}
                              data-testid={`offer-origin-${offer.id}`}
                              sx={{ height: 18, fontSize: 10.5 }}
                            />
                            {offer.overriddenFields.length > 0 && (
                              <Chip
                                size="small"
                                label={`Overridden here · ${offer.overriddenFields
                                  .map((f) => OVERRIDE_FIELD_LABEL[f])
                                  .join(", ")}`}
                                data-testid={`offer-overridden-${offer.id}`}
                                sx={{
                                  height: 18,
                                  fontSize: 10.5,
                                  bgcolor: tokens.brandTintSoft,
                                  color: tokens.brand,
                                }}
                              />
                            )}
                            {offer.suppressed && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label="Hidden here"
                                data-testid={`offer-suppressed-${offer.id}`}
                                sx={{
                                  height: 18,
                                  fontSize: 10.5,
                                  color: tokens.inkFaint,
                                }}
                              />
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {OFFER_KIND_LABEL[offer.action.kind]}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {kindSummary(offer)}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {offer.action.kind === "booking" &&
                    offer.action.slots.mode === "slots"
                      ? "Generated times"
                      : offer.items.length === 0
                        ? "—"
                        : `${offer.items.length}`}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      // The bare word is what gets reported as a bug: an offer
                      // that looks right, reading "Scheduled", with nothing
                      // saying which gate is holding it. Name the gate.
                      label={
                        status === "scheduled" && statusReason
                          ? `${OFFER_STATUS_LABEL[status]} · ${statusReason}`
                          : OFFER_STATUS_LABEL[status]
                      }
                      data-testid={`offer-status-${offer.id}`}
                      sx={{
                        height: 22,
                        fontSize: 11.5,
                        color: STATUS_COLOUR[status],
                        borderColor: STATUS_COLOUR[status],
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {/* Going live is a LIST control, never a side effect of
                        fixing a typo in the editor.

                        On an INHERITED row the same switch means something
                        narrower and says so: it hides the row HERE, and leaves
                        the chain's own switch alone (POS-14). Two controls —
                        one for "on" and one for "hidden at this location" —
                        would be two words for the one question an operator is
                        asking, which is whether guests in THIS building see it.
                        The switch is dead only when the chain itself has
                        switched the row off: suppression is one-way, and a
                        hotel must not put back what its chain withdrew. */}
                    <Tooltip
                      title={
                        editable
                          ? offer.enabled
                            ? "Switch off"
                            : "Switch on"
                          : overridable && onSuppress
                            ? offer.enabled
                              ? offer.suppressed
                                ? "Hidden here — show it at this location again"
                                : "Hide at this location only. The chain keeps it."
                              : "Switched off where it was authored"
                            : "Inherited — switch it off where it was authored"
                      }
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={
                            offer.enabled &&
                            !(offer.inherited && offer.suppressed)
                          }
                          disabled={
                            editable
                              ? false
                              : !(overridable && onSuppress && offer.enabled)
                          }
                          onChange={(e) => {
                            if (editable)
                              return onToggle(offer, e.target.checked);
                            onSuppress?.(offer, !e.target.checked);
                          }}
                          inputProps={{
                            "aria-label": `Show ${
                              readLocalized(offer.title, defaultLocale) ||
                              "this offer"
                            } to guests`,
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {editable && (
                      <RowMenu
                        onEdit={() => onEdit(offer)}
                        onDelete={() => onDelete(offer)}
                      />
                    )}
                    {!editable && overridable && onOverride && (
                      <InheritedRowMenu
                        overridden={
                          offer.overriddenFields.length > 0 || offer.suppressed
                        }
                        onOverride={() => onOverride(offer)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {offers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack alignItems="center" sx={{ py: 5 }} gap={0.5}>
                    {loading ? (
                      <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                        Loading…
                      </Typography>
                    ) : (
                      <>
                        <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
                          Nothing offered yet
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: tokens.inkMuted,
                            textAlign: "center",
                            maxWidth: 460,
                          }}
                        >
                          A promotion is a card the guest reads. An order is a
                          menu they pick from. A booking is an hour they take.
                          Until one exists, the TV shows no Offers tile at all.
                        </Typography>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </PageContainer>
  );
}

function RowMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setEl(e.currentTarget)}
        aria-label="Offer actions"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={el}
        open={Boolean(el)}
        onClose={() => setEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEl(null);
            onEdit();
          }}
        >
          <EditOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEl(null);
            onDelete();
          }}
          sx={{ color: tokens.error }}
        >
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * The row menu an INHERITED row gets (`POS-14`).
 *
 * A separate control from `RowMenu`, with its own label, because it offers a
 * different verb: Edit and Delete would both 404 here — the row belongs to a
 * layer this page cannot write — while "Override for this location" always
 * works. A menu whose items sometimes fail is worse than two menus.
 */
function InheritedRowMenu({
  overridden,
  onOverride,
}: {
  overridden: boolean;
  onOverride: () => void;
}) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setEl(e.currentTarget)}
        aria-label="Location overrides"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={el}
        open={Boolean(el)}
        onClose={() => setEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEl(null);
            onOverride();
          }}
        >
          <TuneIcon fontSize="small" style={{ marginRight: 8 }} />
          {overridden
            ? "Edit this location’s override"
            : "Override for this location…"}
        </MenuItem>
      </Menu>
    </>
  );
}

/* -------------------------------------------------------------------------- */

interface ContainerProps {
  /** Exactly one of the two. A location, or the whole customer. */
  premise?: PremiseConfig;
  companyId?: string;
  companyName?: string;
  companyLocationCount?: number;
}

/**
 * Two operator languages, when there is no premise to ask.
 *
 * A customer is not a place and has no `supportedLocales`; a chain-level offer
 * is written once for hotels that may each declare a different list. Swedish
 * and English are the two Foyer is actually operated in, and a hotel editing
 * the same row at its own layer still sees its own list — so the cost of the
 * guess is an extra empty input, not a lost translation.
 */
const CHAIN_LOCALES = ["sv", "en"];

/**
 * Offers, wired.
 *
 * Both layers arrive in one call — the catalogue route already answers with a
 * location's own rows plus the ones it inherits — so there is no second round
 * trip and no way for the two halves to be out of step with each other.
 */
export default function OffersContainer({
  premise,
  companyId,
  companyName,
  companyLocationCount,
}: ContainerProps) {
  // Either right opens the page (`POS-14`). `canManageOffers` is the chain
  // marketing manager's, mirroring `canManageSignage`; `canEditLayout` is what
  // POS-2 shipped on and every existing operator already holds. The routes
  // accept either, so the CMS must too — a page that hides a control the server
  // would have allowed is the same bug as one that offers a control it refuses.
  // Both read unconditionally: `useCan(a) || useCan(b)` would short-circuit the
  // second HOOK, which React forbids.
  const canManageOffers = useCan("canManageOffers");
  const canEditLayout = useCan("canEditLayout");
  const canEdit = canManageOffers || canEditLayout;
  const scope: OfferScopeRef = useMemo(
    () =>
      premise
        ? { kind: "premise", premiseId: premise.premiseId }
        : { kind: "company", companyId: companyId ?? "" },
    [premise, companyId]
  );
  const scopeKey = offerScopeKey(scope);

  const [offers, setOffers] = useState<RoomOffer[]>([]);
  const [overrides, setOverrides] = useState<RoomOfferOverride[]>([]);
  const [reach, setReach] = useState<OfferReach | undefined>(undefined);
  /** `undefined` = closed. The inherited row being re-decided for this place. */
  const [overriding, setOverriding] = useState<ResolvedRoomOffer | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  /** `undefined` = closed, `null` = creating, a row = editing it. */
  const [editing, setEditing] = useState<RoomOffer | null | undefined>(
    undefined
  );
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<OfferProblem[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** A refusal from a LIST control, where no dialog is open to show it in. */
  const [notice, setNotice] = useState<string | null>(null);
  /** Bumped after every write, so the preview iframe refetches the feed. */
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => {
    let alive = true;
    setLoading(true);
    // The catalogue and this location's deltas, together. In PARALLEL and
    // resolved as one list: fetching the overrides after the rows would draw
    // the chain's price for a moment and then replace it with the hotel's,
    // which reads as a bug in the price rather than as a load.
    Promise.all([
      foyerApi.listRoomOffers(scope),
      scope.kind === "premise"
        ? // Caught on its OWN leg. A blip on the deltas must not blank a
          // catalogue: no overrides means the chain's own prices, which is
          // wrong in one field, while an empty list reads as "this hotel
          // offers nothing" and sends somebody to author it all again.
          foyerApi
            .listOfferOverrides(scope.premiseId)
            .catch(() => [] as RoomOfferOverride[])
        : Promise.resolve<RoomOfferOverride[]>([]),
    ])
      .then(([list, deltas]) => {
        if (!alive) return;
        setOffers(list);
        setOverrides(deltas);
      })
      .catch(() => {
        if (!alive) return;
        setOffers([]);
        setOverrides([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey]);

  useEffect(() => reload(), [reload]);

  /*
   * The blast radius, fetched once per customer.
   *
   * Its own call rather than a field on the catalogue: the banner has to be
   * right before the FIRST save, including on a chain whose catalogue is still
   * empty, and a count folded into a list response would arrive only once there
   * was a list.
   */
  useEffect(() => {
    // Cleared FIRST, on every customer change. This container is not remounted
    // when the scope picker moves, so a stale reach would put chain A's
    // location and room counts under chain B's name until the new call landed
    // — which is exactly the wrong-blast-radius mistake the banner exists to
    // prevent.
    setReach(undefined);
    if (scope.kind !== "company" || !scope.companyId) return;
    let alive = true;
    foyerApi
      .offerReach(scope.companyId)
      .then((r) => alive && setReach(r))
      // Left undefined rather than zeroed: the banner then falls back to the
      // directory's location count and says nothing about rooms. A count of 0
      // rooms would read as a promise that this reaches nobody.
      .catch(() => alive && setReach(undefined));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey]);

  /**
   * The list this page draws — the ONE resolver, the one the television runs.
   *
   * `includeSuppressed` is true here and nowhere else: a row this location has
   * hidden still has to be reachable to be un-hidden. At company scope there is
   * no premise doing the reading, so the rows are tagged by which layer this
   * page can WRITE — global rows arrive read-only there for the same reason a
   * chain row does at a hotel.
   */
  const rows: ResolvedRoomOffer[] = useMemo(() => {
    if (scope.kind === "premise") {
      return resolveRoomOffers({
        offers,
        overrides,
        premiseId: scope.premiseId,
        includeSuppressed: true,
      });
    }
    return offers.map((offer) => ({
      ...offer,
      origin: offer.scope,
      inherited: !isOwnRow(offer, scope),
      suppressed: false,
      overriddenFields: [],
    }));
  }, [offers, overrides, scope]);

  const save = async (body: RoomOfferBody) => {
    setSaving(true);
    setProblems([]);
    setError(null);
    try {
      const saved = editing
        ? await foyerApi.updateRoomOffer(scope, editing.id, body)
        : await foyerApi.createRoomOffer(scope, body);
      // Adopt the server's own row rather than the draft: it stamps ids on new
      // items, re-numbers `order` and normalises the localised strings, and a
      // list still showing the draft would disagree with the next reload.
      setOffers((prev) =>
        prev.some((o) => o.id === saved.id)
          ? prev.map((o) => (o.id === saved.id ? saved : o))
          : [...prev, saved]
      );
      setEditing(undefined);
      setTick((t) => t + 1);
    } catch (e) {
      const refused = offerProblemsOf(e);
      if (refused) setProblems(refused);
      else setError((e as Error).message || "Could not save the offer.");
    } finally {
      setSaving(false);
    }
  };

  /*
   * Both optimistic writes below roll back ONE ROW, never the whole list.
   *
   * Restoring a captured snapshot looks equivalent and is not: two switches
   * flipped in quick succession share a stale closure, so the first one's
   * failure would silently undo the second one's success. A functional update
   * keyed on the row's own id cannot reach a row nobody touched.
   */
  const toggle = async (offer: RoomOffer, enabled: boolean) => {
    setOffers((prev) =>
      prev.map((o) => (o.id === offer.id ? { ...o, enabled } : o))
    );
    try {
      const saved = await foyerApi.updateRoomOffer(scope, offer.id, {
        enabled,
      });
      setOffers((prev) => prev.map((o) => (o.id === saved.id ? saved : o)));
      setTick((t) => t + 1);
    } catch {
      // Put it back rather than leave the switch lying about what guests see.
      setOffers((prev) =>
        prev.map((o) =>
          o.id === offer.id ? { ...o, enabled: offer.enabled } : o
        )
      );
    }
  };

  const remove = async (offer: RoomOffer) => {
    // Where it sat, so a failed delete does not also reshuffle the list. Read
    // inside the updater rather than from the render closure, for the same
    // reason the toggle above uses one.
    let index = -1;
    setOffers((prev) => {
      const found = prev.findIndex((o) => o.id === offer.id);
      if (found >= 0) index = found;
      return prev.filter((o) => o.id !== offer.id);
    });
    try {
      await foyerApi.deleteRoomOffer(scope, offer.id);
      setTick((t) => t + 1);
    } catch {
      setOffers((prev) => {
        const next = [...prev];
        next.splice(index < 0 ? next.length : index, 0, offer);
        return next;
      });
    }
  };

  /* -- POS-14: this location's decisions about inherited rows ----------- */

  /**
   * Write one delta and adopt the server's own row.
   *
   * `patch` REPLACES whatever this location held before, so the dialog always
   * sends the whole of it — see `putOfferOverride`. An empty patch with
   * `suppressed: false` is a legitimate save meaning "no differences here", and
   * it is not turned into a delete: an operator who cleared every field can
   * still see the row is tracked, and reverting is its own button.
   */
  const saveOverride = async (
    offer: ResolvedRoomOffer,
    body: { suppressed?: boolean; patch?: OfferOverridePatch }
  ) => {
    if (scope.kind !== "premise") return;
    setSaving(true);
    setProblems([]);
    setError(null);
    try {
      const saved = await foyerApi.putOfferOverride(
        scope.premiseId,
        offer.id,
        body
      );
      setOverrides((prev) => [
        ...prev.filter((o) => o.offerId !== saved.offerId),
        saved,
      ]);
      setOverriding(undefined);
      setTick((t) => t + 1);
    } catch (e) {
      const refused = offerProblemsOf(e);
      if (refused) setProblems(refused);
      else setError((e as Error).message || "Could not save the override.");
    } finally {
      setSaving(false);
    }
  };

  const revertOverride = async (offer: ResolvedRoomOffer) => {
    if (scope.kind !== "premise") return;
    setSaving(true);
    try {
      await foyerApi.deleteOfferOverride(scope.premiseId, offer.id);
      setOverrides((prev) => prev.filter((o) => o.offerId !== offer.id));
      setOverriding(undefined);
      setTick((t) => t + 1);
    } catch (e) {
      setError((e as Error).message || "Could not remove the override.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Hide, or show again, an inherited row at this location.
   *
   * Un-hiding a row whose delta says nothing else DELETES the delta rather than
   * storing `suppressed: false` — a record that re-decides nothing is a record
   * that would make the row read "Overridden here" forever. A delta that still
   * carries a price keeps it.
   */
  const suppress = async (offer: ResolvedRoomOffer, suppressed: boolean) => {
    if (scope.kind !== "premise") return;
    const patch = overrides.find((o) => o.offerId === offer.id)?.patch ?? {};
    const dropIt = !suppressed && Object.keys(patch).length === 0;
    setNotice(null);
    setSaving(true);
    try {
      if (dropIt) {
        await foyerApi.deleteOfferOverride(scope.premiseId, offer.id);
        setOverrides((prev) => prev.filter((o) => o.offerId !== offer.id));
      } else {
        const saved = await foyerApi.putOfferOverride(
          scope.premiseId,
          offer.id,
          { suppressed, patch }
        );
        setOverrides((prev) => [
          ...prev.filter((o) => o.offerId !== saved.offerId),
          saved,
        ]);
      }
      setTick((t) => t + 1);
    } catch {
      // SAID OUT LOUD, not just rolled back. The switch springing back with no
      // message is how somebody walks away believing a chain campaign is hidden
      // at their hotel while it is still on every screen in the building.
      setNotice(
        suppressed
          ? "Could not hide that offer at this location. It is still showing to guests."
          : "Could not show that offer again at this location."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <OffersPage
        offers={rows}
        scope={scope}
        loading={loading}
        canEdit={canEdit}
        defaultLocale={premise?.defaultLocale ?? CHAIN_LOCALES[0]}
        // A chain has no one clock. Left undefined there, so the status column
        // falls back to UTC rather than pretending one location's zone speaks
        // for four - and every chain row is inherited-read at a location, where
        // it IS resolved in that location's own zone.
        timezone={premise?.address?.timezone}
        chainName={premise ? undefined : companyName}
        chainLocationCount={companyLocationCount}
        chainReach={reach}
        preview={
          premise ? (
            // Remounted on every write so the embedded TV refetches the feed.
            // Offers live in their own collection, not in `PremiseConfig`, so
            // the postMessage channel the preview normally rides has nothing
            // to push — a remount is the only honest refresh.
            <LivePreview key={tick} premise={premise} screen="offers" />
          ) : undefined
        }
        onCreate={() => {
          setProblems([]);
          setError(null);
          setEditing(null);
        }}
        onEdit={(offer) => {
          setProblems([]);
          setError(null);
          setEditing(offer);
        }}
        onDelete={remove}
        onToggle={toggle}
        onOverride={(offer) => {
          setProblems([]);
          setError(null);
          setOverriding(offer);
        }}
        onSuppress={suppress}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
      />
      {editing !== undefined && (
        // Keyed by the record: opening a different one REMOUNTS the dialog, and
        // the remount is the reset. See OfferDialog's docblock.
        <OfferDialog
          key={editing?.id ?? "new"}
          open
          initial={editing}
          supportedLocales={premise?.supportedLocales ?? CHAIN_LOCALES}
          defaultLocale={premise?.defaultLocale ?? CHAIN_LOCALES[0]}
          saving={saving}
          serverProblems={problems}
          error={error}
          onClose={() => setEditing(undefined)}
          onSave={save}
        />
      )}
      {overriding && (
        // `original` is the RAW row off the catalogue, not the resolved one:
        // the dialog has to say what each field would go back to, and the
        // resolved row is already wearing this location's answers.
        <OfferOverrideDialog
          key={overriding.id}
          offer={overriding}
          original={offers.find((o) => o.id === overriding.id) ?? overriding}
          saving={saving}
          serverProblems={problems}
          error={error}
          onClose={() => setOverriding(undefined)}
          onSave={(patch) =>
            saveOverride(overriding, {
              suppressed: overriding.suppressed,
              patch,
            })
          }
          onRevert={
            overriding.overrideId ? () => revertOverride(overriding) : undefined
          }
        />
      )}
    </>
  );
}
